import type { Plugin } from "unified";
import type { Root as MdastRoot, Text as MdastText } from "mdast";
import { CONTINUE, visit, Visitor } from "unist-util-visit";
import type { Options as RemarkRehypeOptions } from "remark-rehype";

const widgetRegex = /::widget:([\w-]+):([\w.,-]+)::/g;

export const remarkRehypeOptions: Readonly<RemarkRehypeOptions> = {
  // @ts-expect-error :: our specific node is not documented in the RemarkRehypeOptions types, as expected
  passThrough: ["widget"],
};

const myVisitor: Visitor<MdastText> = (node, index, parent) => {
  if (parent === null || index === null || index === undefined) return;

  const textValue = node.value as string;
  let previousIndex = 0;
  const newChildren: Array<
    | MdastText
    | { type: string; widgetType: string; widgetId: string }
    | { type: string; widgetType: string; widgetIds: string[] }
  > = [];
  let match: RegExpExecArray | null;

  while ((match = widgetRegex.exec(textValue)) !== null) {
    const fullWidgetText = match[0]; // e.g., ::widget:image:123:: or ::widget:citation-group:id1,id2::
    const widgetType = match[1]; // e.g., image or citation-group
    const widgetId = match[2]; // e.g., 123 or id1,id2,id3
    const matchStartIndex = match.index;

    // Text before the widget
    if (matchStartIndex > previousIndex) {
      newChildren.push({
        type: "text",
        value: textValue.slice(previousIndex, matchStartIndex),
      });
    }

    // Handle citation-group with comma-separated IDs
    if (widgetType === "citation-group") {
      const widgetIds = widgetId.split(",");
      newChildren.push({
        type: "widget",
        widgetType: "citation-group",
        widgetIds,
      });
    } else {
      // Regular widget node
      newChildren.push({
        type: "widget",
        widgetType,
        widgetId,
      });
    }

    previousIndex = matchStartIndex + fullWidgetText.length;
  }

  // Text after the last widget
  if (previousIndex < textValue.length) {
    newChildren.push({
      type: "text",
      value: textValue.slice(previousIndex),
    });
  }

  // Replace the original text node with the new children
  parent?.children.splice(index, 1, ...newChildren);
  return CONTINUE;
};

/**
 * Parses widget placeholders in markdown.
 *
 * For example, it can parse a placeholder like `::widget:image:123::` and replace it in the AST with a widget object.
 *
 */
export const widgetRemarkPlugin: Plugin<[], MdastRoot> = () => {
  return (tree) => {
    visit(tree, "text", myVisitor);
  };
};
